import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { API_CONSTANTS, MESSAGE_CONSTANTS, STATUS_CODES } from '../constants';
import { NightwebUtil } from '../utils';
import { HttpClientService } from './http-client.service';
import { Router } from '@angular/router';
import { ToasterService } from './toaster.service';
import { OrganizationSettingsService } from './organization-settings.service';
import { OrganizationService } from './organization.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService {

  user!: any;

  constructor(
    private router: Router,
    private http: HttpClientService,
    private nightwebUtil: NightwebUtil,
    private toasterService: ToasterService,
    private organizationSettings: OrganizationSettingsService,
    private organizationService: OrganizationService,
  ) { }

  public isAuthenticated(): boolean {
    this.user = this.nightwebUtil.getLoggedInUser();
    if (this.user) {
      return true;
    } else {
      return false;
    }
  }

  public isRoleAuthorized(permission: any): boolean {
    return true;
  }

  login(credentials: any) {
    return this.http.post(`${API_CONSTANTS.AUTH}${API_CONSTANTS.LOGIN}`, credentials).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  register(body: any) {
    return this.http.post(`${API_CONSTANTS.AUTH}${API_CONSTANTS.REGISTER}`, body).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  verifyLoginCode(data: any) {
    return this.http.post(`${API_CONSTANTS.AUTH}${API_CONSTANTS.VERIFY_CODE}`, data).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  resendLoginCode(username: string) {
    return this.http.post(`${API_CONSTANTS.AUTH}${API_CONSTANTS.RESEND_CODE}`, {
      email: username
    }).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  forgotPassword(username: string) {
    return this.http.post(`${API_CONSTANTS.AUTH}${API_CONSTANTS.FORGOT_PASSWORD}`, username).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  changePassword(data: any) {
    return this.http.patch(`${API_CONSTANTS.AUTH}${API_CONSTANTS.CHANGE_PASSWORD}`, data).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  resetPassword(body: any) {
    return this.http.post(`${API_CONSTANTS.AUTH}${API_CONSTANTS.SET_PASSWORD}`, body).pipe(
      map((response: any) => {
        return response.data;
      })
    );
  }

  refresh() {
    return this.http.get(`${API_CONSTANTS.AUTH}${API_CONSTANTS.REFRESH}`).pipe(
      map((response: any) => {
        return response;
      })
    );
  }

  logout() {
    return this.http.get(`${API_CONSTANTS.AUTH}${API_CONSTANTS.LOGOUT}`)
      .subscribe(() => {
        this.organizationSettings.reset();
        this.nightwebUtil.clearStorage();
        window.location.href = '/login';
      });
  }

  /**
   * Post-login path for end users: use threat-pair flag from plainUser/JWT when
   * already applied; otherwise load the org by id and then resolve the path.
   */
  resolvePostLoginPath(): Observable<string> {
    if (
      !this.nightwebUtil.isEndUser() ||
      this.nightwebUtil.isInvitedEndUser()
    ) {
      return of(this.nightwebUtil.getPostLoginPath());
    }

    if (this.organizationSettings.threatPairModuleEnabled !== null) {
      return of(this.nightwebUtil.getEndUserHomePath());
    }

    const orgId = this.nightwebUtil.getLoginOrganizationId();
    if (!orgId) {
      return of(this.nightwebUtil.getEndUserHomePath());
    }

    return this.organizationService.getOrganizationById(orgId).pipe(
      map((res: any) => {
        this.organizationSettings.applyFromOrganizationPayload(res?.data);
        return this.nightwebUtil.getEndUserHomePath();
      }),
      catchError(() => of(this.nightwebUtil.getEndUserHomePath())),
    );
  }
}
